import type Highcharts from 'highcharts/esm/highstock'
import { isValidDate } from '@/helpers/common/validationHelper'

const TOLERANCE = 1000 // 1 second
const DATA_GAPS_MINIMUM_SIZE = 3

export const defaultGraphColors: string[] = [
  `rgb(27, 128, 196)`,

  `rgb(228, 57, 242)`,
  `rgb(243, 110, 58)`,
  `rgb(131, 169, 63)`,
  `rgb(247, 31, 31)`
]

declare module 'highcharts' {
  interface Chart {
    containerWidth?: number
    customTooltip?: Highcharts.SVGElement
  }
}

export const getTooltipFormatterCallback = (
  units: string
): Highcharts.TooltipFormatterCallbackFunction => {
  units = units.replace('^', '<sup>')
  units += '</sup>'

  return function (_tooltip: Highcharts.Tooltip): string {
    const value =
      this.series.options.type === 'flags'
        ? (this as unknown as GraphFlagPoint).text
        : this.y + units
    const date = new Date(this.x).toLocaleString()
    const color = this.series.color as string
    return `<span>${date}</span><br /><span><b style="color: ${color};">${value}</b></span>`
  }
}

export const defaultGraphOptions: Highcharts.Options = {
  accessibility: { enabled: false },
  chart: {
    zooming: { type: 'x' },
    animation: false,
    height: null
  },
  credits: { enabled: false },
  boost: {
    enabled: true,
    usePreallocated: true
  },
  title: { text: '' },
  legend: { enabled: false },
  subtitle: { text: '' },
  tooltip: {
    valueDecimals: 2,
    borderColor: `rgb(27, 128, 196)`,
    useHTML: true
    // formatter deliberately omitted - keeps this object structuredClone-able
    // (see getDefaultGraphOptions() below), functions can't be cloned.
  },
  time: { timezone: undefined },
  xAxis: {
    type: 'datetime',
    dateTimeLabelFormats: {
      millisecond: { main: '%l:%M:%S.%L %p' },
      second: { main: '%l:%M:%S %p' },
      minute: { main: '%l:%M %p' },
      hour: { main: '%l:%M %p' },
      day: { main: '%e. %b' },
      week: { main: '%e. %b' },
      month: { main: "%b '%y" },
      year: { main: '%Y' }
    }
  },
  yAxis: {
    title: { text: '' },
    labels: { enabled: true }
  },
  series: [],
  navigator: {
    enabled: true,
    height: 30,
    margin: 16,
    xAxis: {
      dateTimeLabelFormats: {
        millisecond: { main: '%l:%M:%S.%L %p' },
        second: { main: '%l:%M:%S %p' },
        minute: { main: '%l:%M %p' },
        hour: { main: '%l:%M %p' },
        day: { main: '%e. %b' },
        week: { main: '%e. %b' },
        month: { main: "%b '%y" },
        year: { main: '%Y' }
      }
    }
  },
  scrollbar: { enabled: false },
  rangeSelector: { enabled: false },
  plotOptions: {
    series: {
      showInNavigator: true,
      opacity: 1,
      states: {
        inactive: {
          enabled: false
        }
      }
    }
  }
}

export const getDefaultGraphOptions = (units = ''): Highcharts.Options => {
  const options = structuredClone(defaultGraphOptions)
  options.tooltip = {
    ...options.tooltip,
    formatter: getTooltipFormatterCallback(units)
  }
  return options
}

const getInvalidAQIStartTimestamp = (readings: [number, number | null][]): number[] => {
  const timestamps: number[] = []
  for (let i = 0; i < readings.length; i++) {
    const timestamp = readings[i][0]
    const aqi = readings[i][1]
    if (aqi === null) {
      if (i === 0) {
        timestamps.push(timestamp)
      } else {
        const prevAqi = readings[i - 1][1]
        if (prevAqi !== null) {
          timestamps.push(timestamp)
        }
      }
    }
  }
  return timestamps
}

export interface GraphFlagPoint {
  x: number
  title: string
  text: string
}

export interface GraphFlag {
  type: 'flags'
  data: GraphFlagPoint[]
  shape: string
  color: string
  fillColor: string
  width: number
  style: Record<string, string>
  name: string
}

export const createFlags = (
  readings: [number, number | null][],
  color: string,
  invalidAqiText: string
): GraphFlag | null => {
  const invalidAQITimestamp = getInvalidAQIStartTimestamp(readings)
  if (invalidAQITimestamp.length > 0) {
    return {
      type: 'flags',
      data: invalidAQITimestamp.map((t) => ({ x: t, title: '!', text: invalidAqiText })),
      shape: 'circlepin',
      color,
      fillColor: color,
      width: 16,
      style: { color: 'white' },
      name: `AQI flag ${color}`
    }
  }
  return null
}

const approxEq = (pt1: number, pt2: number): boolean => {
  return Math.abs(pt1 - pt2) <= TOLERANCE || pt2 === 0
}

export const getIntervalWithoutDateMilliseconds = (
  timestamp1: number | string | null | undefined,
  timestamp2: number | string | null | undefined
): number => {
  const date1 = timestamp1 ? new Date(timestamp1) : null
  const date2 = timestamp2 ? new Date(timestamp2) : null

  if (isValidDate(date1) && isValidDate(date2)) {
    date1.setMilliseconds(0)
    date2.setMilliseconds(0)
    return date2.getTime() - date1.getTime()
  }

  return 0
}

export const hasMinimumSizeForDataGaps = (arr: unknown[] | null | undefined): boolean => {
  return (arr?.length ?? 0) >= DATA_GAPS_MINIMUM_SIZE
}

export interface Interval {
  timestamp1: number
  timestamp2: number
  interval: number
}

export const findIntervals = (arr: [number, unknown][]): Interval[] => {
  const intervals: Interval[] = []
  if (arr?.length >= 2) {
    for (let i = 0; i < arr.length - 1; i++) {
      const timestamp1 = arr[i][0]
      const timestamp2 = arr[i + 1][0]
      intervals.push({
        timestamp1,
        timestamp2,
        interval: getIntervalWithoutDateMilliseconds(timestamp1, timestamp2)
      })
    }
  }
  return intervals
}

export type GapResult = [[number, unknown][], number[], number[]]

export const findGaps = (arr: [number, unknown][]): GapResult => {
  const gapTSArr: number[] = []
  const intervalChangeTSArr: number[] = []

  if (hasMinimumSizeForDataGaps(arr)) {
    const intervals = findIntervals(arr)
    let interval = intervals[0].interval
    for (let i = 0; i < intervals.length; i++) {
      const currInterval = intervals[i].interval
      const isIntervalTheSame = approxEq(interval, currInterval)
      if (!isIntervalTheSame) {
        intervalChangeTSArr.push(intervals[i].timestamp1)
        const nextInterval = i === intervals.length - 1 ? -1 : intervals[i + 1].interval
        const isNextIntervalTheSame = approxEq(currInterval, nextInterval)
        if (isNextIntervalTheSame) {
          interval = currInterval
        } else {
          gapTSArr.push(
            intervals[i].timestamp2 - (intervals[i].timestamp2 - intervals[i].timestamp1) / 2
          )
        }
      }
    }
  }
  return [arr, gapTSArr, intervalChangeTSArr]
}

export interface TooltipPosition {
  x: number
  y: number
}

export const getCustomTooltipPosition = (
  event: MouseEvent | null | undefined,
  graph: Highcharts.Chart,
  labelWidth: number
): TooltipPosition => {
  let x = 0
  let y = 0
  if (event && typeof event === 'object' && graph) {
    const { pageX, pageY } = event
    const { plotLeft, plotTop, containerWidth } = graph

    // Get the bounding rectangle of the chart container
    const containerRect = graph.container.getBoundingClientRect()

    // Calculate position of the data label relative to the container
    x = pageX - containerRect.left - plotLeft + 50
    const labelRight = x + labelWidth
    // If the label is out of the graph container
    if (labelRight > containerWidth) {
      x = x - (labelRight - containerWidth)
    }

    y = Math.max(pageY - containerRect.top - plotTop - 20, 0)
  }

  return { x, y }
}

interface TooltipConfig {
  labelText: string
  x: number
  y: number
  attr: Record<string, unknown>
  css: Record<string, string>
}

const renderCustomTooltip = (
  graph: Highcharts.Chart,
  tooltipConfig: TooltipConfig
): Highcharts.SVGElement | null => {
  if (graph && typeof graph.renderer === 'object') {
    return graph.renderer
      .label(tooltipConfig.labelText, tooltipConfig.x, tooltipConfig.y)
      .attr(tooltipConfig.attr)
      .css(tooltipConfig.css)
      .add()
  }
  return null
}

export const renderGapInData = (
  graph: Highcharts.Chart,
  x: number,
  y: number
): Highcharts.SVGElement | null => {
  return renderCustomTooltip(graph, {
    labelText: 'Gap in Data',
    attr: {
      'stroke-width': 1,
      zIndex: 5,
      stroke: `rgb(27, 128, 196)`,
      padding: 8,
      r: 5,
      fill: 'rgba(248, 248, 248, 0.85)',
      shadow: true
    },
    css: {
      color: '#333',
      fontSize: '12px'
    },
    x,
    y
  })
}

const createPlotLines = (
  gap: number,
  color: string,
  graph: Highcharts.Chart
): Highcharts.XAxisPlotLinesOptions[] => {
  return [
    {
      color,
      width: 1,
      value: gap,
      dashStyle: 'Dash'
    },
    {
      color: 'transparent',
      width: 5,
      value: gap,
      dashStyle: 'Solid',
      events: {
        mouseover(e) {
          if (graph && !graph.customTooltip) {
            const { x, y } = getCustomTooltipPosition(e as MouseEvent, graph, 100)
            graph.customTooltip = renderGapInData(graph, x, y)
          }
        },
        mouseout() {
          if (graph?.customTooltip) {
            graph.customTooltip.destroy()
            graph.customTooltip = null
          }
        }
      }
    }
  ]
}

export const getPlotLines = (
  readings: [number, unknown][],
  seriesColor: string,
  graph: Highcharts.Chart
): Highcharts.XAxisPlotLinesOptions[] => {
  const plotLines: Highcharts.XAxisPlotLinesOptions[] = []
  if (hasMinimumSizeForDataGaps(readings)) {
    readings.forEach((currentPoint, idx) => {
      if (idx > 1 && currentPoint[1] === null) {
        const [preGapTime] = readings[idx - 1]
        plotLines.push(...createPlotLines(preGapTime, seriesColor, graph))
      }
    })
  }
  return plotLines
}
